import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-8">
    <h2 className="font-heading text-xl font-bold mb-3 text-foreground">{title}</h2>
    <div className="text-muted-foreground text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const PrivacyPolicyPage = () => (
  <>
    <Helmet>
      <title>Privacy Policy | Debnath Telecom</title>
      <meta name="description" content="Privacy Policy for Debnath Telecom & Variety Store — debnathtelecom.in" />
    </Helmet>

    <div className="container max-w-3xl py-12 px-4">
      <div className="mb-8">
        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Legal</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">Last updated: April 2025 &nbsp;|&nbsp; <a href="https://debnathtelecom.in" className="text-primary hover:underline">debnathtelecom.in</a></p>
      </div>

      <div className="prose-sm max-w-none">
        <Section title="1. Introduction">
          <p>Welcome to Debnath Telecom & Variety Store ("we", "our", "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website <strong>debnathtelecom.in</strong> or make a purchase from us.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect information you provide directly to us, including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Full name, email address, and phone number</li>
            <li>Delivery address (street, city, state, PIN code)</li>
            <li>Order history and purchase details</li>
            <li>Account credentials (email and encrypted password)</li>
          </ul>
          <p>We also automatically collect certain technical information such as IP address, browser type, and pages visited for analytics purposes.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Process and fulfill your orders</li>
            <li>Send order confirmations and delivery updates</li>
            <li>Respond to customer service requests</li>
            <li>Improve our website and product offerings</li>
            <li>Send promotional communications (only with your consent)</li>
          </ul>
        </Section>

        <Section title="4. Sharing of Information">
          <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Delivery partners to fulfill your orders</li>
            <li>Payment processors (Razorpay) for secure transactions</li>
            <li>Legal authorities when required by law</li>
          </ul>
        </Section>

        <Section title="5. Data Security">
          <p>We implement industry-standard security measures to protect your personal data. Your account password is encrypted and never stored in plain text. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
        </Section>

        <Section title="6. Cookies">
          <p>Our website uses cookies to enhance your browsing experience, remember your preferences, and analyze site traffic. You can disable cookies in your browser settings, though some features of the website may not function properly.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Opt out of marketing communications at any time</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:chinmaydebnath21@gmail.com" className="text-primary hover:underline">chinmaydebnath21@gmail.com</a>.</p>
        </Section>

        <Section title="8. Third-Party Links">
          <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date. Continued use of our website after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="10. Contact Us">
          <p>If you have any questions about this Privacy Policy, please contact us:</p>
          <ul className="list-none space-y-1">
            <li>📧 <a href="mailto:chinmaydebnath21@gmail.com" className="text-primary hover:underline">chinmaydebnath21@gmail.com</a></li>
            <li>📞 +91 96792 00493</li>
            <li>📍 Kharagpur City Road, Baga Bhera, West Bengal, Pin 721437, India</li>
          </ul>
        </Section>
      </div>
    </div>
  </>
);

export default PrivacyPolicyPage;
